import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { BookOpen, RefreshCw, Upload } from 'lucide-react'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  QueryErrorState,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
} from '@/components/ui'
import { useQuestionnaires } from '../api/queries'
import type { QuestionnaireMode, QuestionnaireRow } from '../api/types'
import { UploadDrawer } from '../components/UploadDrawer'
import { STAGE_META, formatDate, isOverdue } from '../utils/meta'

/** /regulatory/questionnaires — the log of questionnaires received. */
export function QuestionnairesPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [stage, setStage] = useState('')
  const [mode, setMode] = useState<QuestionnaireMode | ''>('')
  const [uploadOpen, setUploadOpen] = useState(false)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const { data, isLoading, isFetching, isError, error, refetch } = useQuestionnaires({
    stage: stage || undefined,
    mode: mode || undefined,
    q: debounced || undefined,
  })
  const rows = useMemo(() => data?.items ?? [], [data])
  const canEdit = data?.can_edit ?? false
  const open = (id: string) =>
    navigate({ to: '/regulatory/questionnaires/$questionnaireId', params: { questionnaireId: id } })

  const inReview = rows.filter((r) => r.stage === 'in_review').length
  const overdue = rows.filter((r) => isOverdue(r.due_on, r.stage)).length
  const toAnswer = rows.reduce(
    (n, r) =>
      n +
      (r.stage === 'in_review' ? (r.counts?.unanswered ?? 0) + (r.counts?.needs_review ?? 0) : 0),
    0,
  )

  const columns = useMemo<ColumnDef<QuestionnaireRow, unknown>[]>(
    () => [
      {
        id: 'title',
        header: 'Questionnaire',
        accessorKey: 'title',
        cell: (ctx) => (
          <span className="flex items-center gap-2">
            <span className="text-role-data-primary">{ctx.row.original.title}</span>
            {ctx.row.original.mode === 'import' ? <Chip size="xs">Library import</Chip> : null}
          </span>
        ),
      },
      {
        id: 'requester',
        header: 'Customer',
        accessorFn: (r) => r.requester_name ?? '',
        cell: (ctx) => ctx.row.original.requester_name ?? <span className="text-tertiary">—</span>,
      },
      {
        id: 'stage',
        header: 'Stage',
        accessorKey: 'stage',
        cell: (ctx) => {
          const meta = STAGE_META[ctx.row.original.stage]
          return (
            <Chip variant={meta.variant} dot title={ctx.row.original.error_message ?? undefined}>
              {meta.label}
            </Chip>
          )
        },
      },
      {
        id: 'answers',
        header: 'Answers',
        enableSorting: false,
        cell: (ctx) => {
          const c = ctx.row.original.counts
          if (!c || !c.total) return <span className="text-tertiary">—</span>
          return (
            <span className="flex items-center gap-1.5">
              <Chip size="xs" variant="success" title="Answered from the library">
                {c.reused} library
              </Chip>
              {c.needs_review ? (
                <Chip size="xs" variant="warning" title="Answers to check">
                  {c.needs_review} check
                </Chip>
              ) : null}
              {c.unanswered ? (
                <Chip size="xs" variant="danger" title="No answer yet">
                  {c.unanswered} open
                </Chip>
              ) : null}
              <span className="text-tertiary">of {c.total}</span>
            </span>
          )
        },
      },
      {
        id: 'received_on',
        header: 'Received',
        accessorFn: (r) => r.received_on ?? '',
        cell: (ctx) => formatDate(ctx.row.original.received_on),
      },
      {
        id: 'due_on',
        header: 'Due',
        accessorFn: (r) => r.due_on ?? '',
        cell: (ctx) => {
          const r = ctx.row.original
          return isOverdue(r.due_on, r.stage) ? (
            <Chip size="xs" variant="danger">
              {formatDate(r.due_on)} overdue
            </Chip>
          ) : (
            formatDate(r.due_on)
          )
        },
      },
      {
        id: 'assigned_to',
        header: 'Assigned',
        accessorFn: (r) => r.assigned_to ?? '',
        cell: (ctx) =>
          ctx.row.original.assigned_to?.split('@')[0] ?? <span className="text-tertiary">—</span>,
      },
    ],
    [],
  )

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <PageHeaderBand
        title="Customer Questionnaires"
        description="Vendor audits and supplier questionnaires, answered from SPI's approved answer library."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard variant="inline" label="In review" value={inReview.toLocaleString()} />
          <MetricCard variant="inline" label="Answers to do" value={toAnswer.toLocaleString()} />
          <MetricCard variant="inline" label="Overdue" value={overdue.toLocaleString()} />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1 flex-col">
        <Toolbar>
          <SearchInput
            containerClassName="flex-1 max-w-none"
            placeholder="Search title or customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
          <Button
            variant="secondary"
            onClick={() => navigate({ to: '/regulatory/questionnaires/library' })}
          >
            <BookOpen size={14} /> Answer library
          </Button>
          <Button variant="primary" onClick={() => setUploadOpen(true)}>
            <Upload size={14} /> Upload questionnaire
          </Button>
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <Select aria-label="Stage" value={stage} onChange={(e) => setStage(e.target.value)}>
              <option value="">All stages</option>
              {Object.entries(STAGE_META).map(([key, meta]) => (
                <option key={key} value={key}>
                  {meta.label}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Kind"
              value={mode}
              onChange={(e) => setMode(e.target.value as QuestionnaireMode | '')}
            >
              <option value="">All questionnaires</option>
              <option value="new">Customer questionnaires</option>
              <option value="import">Library imports</option>
            </Select>
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>{rows.length.toLocaleString()} questionnaires</ToolbarCount>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={isFetching && !isLoading}
              onClick={() => refetch()}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
          {isError ? (
            <QueryErrorState
              error={error}
              onRetry={() => void refetch()}
              subject="questionnaires"
            />
          ) : rows.length === 0 && !isLoading ? (
            <EmptyState
              variant={debounced || stage ? 'no-match' : 'no-data'}
              title={debounced || stage ? 'No questionnaires match' : 'No questionnaires yet'}
              description={
                canEdit
                  ? 'Upload one to answer, or import questionnaires SPI already answered to build the library.'
                  : 'Upload a questionnaire a customer sent you.'
              }
              action={
                <Button variant="secondary" onClick={() => setUploadOpen(true)}>
                  <Upload size={14} /> Upload questionnaire
                </Button>
              }
            />
          ) : (
            <DataTable
              data={rows}
              columns={columns}
              getRowId={(r) => r.id}
              onRowClick={(r) => open(r.id)}
              moduleId="questionnaires"
              maxHeight="100%"
              minTableWidth={900}
            />
          )}
        </div>
      </div>

      <UploadDrawer
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        canImport={canEdit}
        onUploaded={(row) => open(row.id)}
      />
    </div>
  )
}
